import { Amplify } from 'aws-amplify';
import {
  fetchAuthSession,
  getCurrentUser,
  signIn,
  signOut,
} from 'aws-amplify/auth';
import {
  api,
  ApiError,
  setAdminIdToken,
  setAdminIdTokenEnsured,
  setAdminIdTokenRefresher,
  type AdminMe,
} from './api';

let configured = false;
let ensureInflight: Promise<string | null> | null = null;
let cachedMe: AdminMe | null = null;

/** Full catalog — used only when /admin/me is not deployed yet (404). */
const TRANSITIONAL_ADMIN_PERMISSIONS = [
  'dashboard.view',
  'users.view',
  'users.manage',
  'users.suspend',
  'users.remove',
  'users.assign_roles',
  'users.grant_admin',
  'roles.manage',
  'sessions.view',
  'sessions.view_identity',
  'sessions.view_transcript',
  'sessions.view_perception',
  'sessions.view_admin_owned',
  'sessions.view_autotest',
  'sessions.observe_live',
  'sessions.control_live',
  'sessions.end',
  'sessions.export',
  'sessions.export_report',
  'sessions.delete',
  'agents.view',
  'agents.manage',
  'agents.publish',
  'agents.delete',
  'agents.test_call',
  'perception.view',
  'perception.manage',
  'recordings.view',
  'recordings.download',
  'recordings.delete',
  'checkups.view',
  'checkups.manage',
  'incidents.view',
  'incidents.manage',
  'settings.view',
  'settings.manage',
  'providers.manage',
  'integrations.manage',
  'secrets.manage',
  'security.manage',
  'audit.view',
  'audit.export',
  'finance.view',
  'ui.beta_features',
  'ui.notifications',
  'ui.env_switcher',
  'ui.support',
  'account.view',
];

export type AuthProfile = {
  email: string;
  groups: string[];
  /** True when the user may access the Admin app (ACTIVE platform user with permissions). */
  isAdmin: boolean;
  firstName: string;
  lastName: string;
  roleName: string;
  roleId: string;
  userId: string;
  permissions: string[];
  /** Empty = no agents. */
  allowedAgentIds: string[];
  status: string;
};

export async function ensureAmplifyConfigured(): Promise<void> {
  if (configured) return;
  const cfg = await api.authConfig();
  if (!cfg.userPoolId || !cfg.userPoolClientId) {
    throw new Error(
      'Cognito pool not configured on API. If this is local dev, run Admin with vite proxy (port 5176). '
        + 'If this is production, rebuild Admin with VITE_API_URL set to the control API.',
    );
  }
  Amplify.configure({
    Auth: {
      Cognito: {
        userPoolId: cfg.userPoolId,
        userPoolClientId: cfg.userPoolClientId,
      },
    },
  });
  configured = true;
}

/**
 * Soft session refresh — Amplify renews when access/id tokens are expired
 * (as long as the refresh token is still valid).
 */
export async function refreshAdminToken(): Promise<string | null> {
  await ensureAmplifyConfigured();
  try {
    await getCurrentUser();
    const session = await fetchAuthSession({ forceRefresh: false });
    const token = session.tokens?.idToken?.toString() || null;
    setAdminIdToken(token);
    return token;
  } catch {
    setAdminIdToken(null);
    return null;
  }
}

/** Force Cognito refresh (used after API 401 or when JWT is near expiry). */
export async function forceRefreshAdminToken(): Promise<string | null> {
  await ensureAmplifyConfigured();
  try {
    await getCurrentUser();
    const session = await fetchAuthSession({ forceRefresh: true });
    const token = session.tokens?.idToken?.toString() || null;
    setAdminIdToken(token);
    return token;
  } catch {
    setAdminIdToken(null);
    return null;
  }
}

/** Deduped soft/force refresh for API calls when the cached Bearer is stale. */
export async function ensureAdminTokenFresh(): Promise<string | null> {
  if (ensureInflight) return ensureInflight;
  ensureInflight = (async () => {
    // Prefer force when we already know the in-memory JWT is dead — soft
    // refresh can return the same expired idToken from Amplify's cache.
    const forced = await forceRefreshAdminToken();
    if (forced) return forced;
    return refreshAdminToken();
  })().finally(() => {
    ensureInflight = null;
  });
  return ensureInflight;
}

setAdminIdTokenRefresher(forceRefreshAdminToken);
setAdminIdTokenEnsured(ensureAdminTokenFresh);

function profileFromMe(me: AdminMe, groups: string[]): AuthProfile {
  const permissions = Array.isArray(me.permissions) ? me.permissions : [];
  const active = String(me.status || 'ACTIVE').toUpperCase() === 'ACTIVE';
  return {
    email: me.email || '',
    groups,
    isAdmin: active && permissions.length > 0,
    firstName: me.firstName || '',
    lastName: me.lastName || '',
    roleName: me.roleName || '',
    roleId: me.roleId || '',
    userId: me.userId || '',
    permissions,
    allowedAgentIds: Array.isArray(me.allowedAgentIds) ? me.allowedAgentIds.map(String) : [],
    status: me.status || 'ACTIVE',
  };
}

export function clearAuthMeCache() {
  cachedMe = null;
}

function isMeEndpointMissing(err: unknown): boolean {
  if (err instanceof ApiError) {
    return err.status === 404 || /not found/i.test(err.message);
  }
  if (err instanceof Error) return /not found/i.test(err.message);
  return false;
}

export async function getAuthProfile(): Promise<AuthProfile | null> {
  const token = await refreshAdminToken();
  if (!token) {
    cachedMe = null;
    return null;
  }
  const session = await fetchAuthSession();
  const payload = session.tokens?.idToken?.payload || {};
  const groups = (payload['cognito:groups'] as string[]) || [];
  const email = String(payload.email || payload['cognito:username'] || '');
  try {
    const me = await api.me.get();
    cachedMe = me;
    return profileFromMe(me, groups);
  } catch (err) {
    cachedMe = null;
    // Deploy gap: /admin/me is not live yet. Keep Cognito admin group as the coarse gate
    // so operators are not locked out mid-rollout. Once /admin/me exists, platform RBAC wins.
    if (isMeEndpointMissing(err) && groups.includes('admin')) {
      return {
        email,
        groups,
        isAdmin: true,
        firstName: email.split('@')[0] || 'Admin',
        lastName: '',
        roleName: 'Administrator',
        roleId: 'role_administrator',
        userId: String(payload.sub || ''),
        permissions: [...TRANSITIONAL_ADMIN_PERMISSIONS],
        allowedAgentIds: [],
        status: 'ACTIVE',
      };
    }
    return null;
  }
}

export function getCachedMe(): AdminMe | null {
  return cachedMe;
}

export async function adminSignIn(email: string, password: string): Promise<AuthProfile> {
  await ensureAmplifyConfigured();
  // Prefer USER_PASSWORD_AUTH — matches Cognito client authFlows.userPassword
  await signIn({
    username: email,
    password,
    options: { authFlowType: 'USER_PASSWORD_AUTH' },
  });
  // Record append-only audit sign-in once (login=1), then load profile as usual.
  try {
    const me = await api.me.get({ login: true });
    cachedMe = me;
  } catch {
    /* profile load below still runs */
  }
  const profile = await getAuthProfile();
  if (!profile?.isAdmin) {
    throw new Error('Signed in but this account has no Admin access');
  }
  return profile;
}

export async function adminSignOut(): Promise<void> {
  try {
    await ensureAmplifyConfigured();
    await signOut();
  } finally {
    cachedMe = null;
    setAdminIdToken(null);
  }
}
